import { useState, useEffect } from "react";
import { toast } from "sonner";
import { QrCode, Printer, Copy, ExternalLink, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PoweredBy } from "@/components/VinconexMark";

export default function QRModal({ vehicle, open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/vehicles/${vehicle.id}/qr`);
      setData(res.data);
    } catch {
      toast.error("Could not generate QR code");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (o) => {
    onOpenChange(o);
    if (o && !data) load();
  };

  useEffect(() => {
    if (open && !data && !loading) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, vehicle?.id]);

  const copy = () => {
    navigator.clipboard.writeText(data.url);
    toast.success("Landing page link copied");
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-[#0F111A] border-[#2A2E3D] text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading font-extrabold flex items-center gap-2">
            <QrCode className="h-5 w-5 text-[#0A8CF2]" /> Vehicle QR Code
          </DialogTitle>
          <DialogDescription className="sr-only">Scannable QR code linking to the vehicle landing page.</DialogDescription>
        </DialogHeader>
        <div className="text-center">
          <p className="text-sm text-slate-400 mb-4">
            {vehicle.year} {vehicle.make} {vehicle.model} {vehicle.trim}
          </p>
          {loading || !data ? (
            <div className="h-64 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-[#0A8CF2]" />
            </div>
          ) : (
            <>
              <div className="bg-white rounded-2xl p-4 inline-block mx-auto">
                <img src={data.qr} alt="QR code" data-testid="vehicle-qr-image" className="w-60 h-auto" />
              </div>
              <p className="mt-2 text-[11px] text-slate-500" data-testid="qr-ids-caption">
                Stock #{vehicle.stock_number || "—"} and VIN are printed into the code
              </p>
              <p className="mt-4 text-xs font-mono-tech text-slate-500 break-all px-4">{data.url}</p>
              <div className="grid grid-cols-3 gap-2 mt-5">
                <Button
                  variant="outline"
                  onClick={copy}
                  data-testid="qr-copy-link-btn"
                  className="border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-200"
                >
                  <Copy className="h-4 w-4" />
                </Button>
                <a href={data.url} target="_blank" rel="noreferrer">
                  <Button
                    variant="outline"
                    className="w-full border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-200"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </a>
                <a href={`/sticker/${vehicle.id}`} target="_blank" rel="noreferrer">
                  <Button
                    data-testid="generate-qr-print-sheet-btn"
                    className="w-full bg-[#0A8CF2] hover:bg-[#3AA3FF] font-bold"
                  >
                    <Printer className="h-4 w-4" />
                  </Button>
                </a>
              </div>
              <p className="text-[11px] text-slate-600 mt-3">Copy link · Preview · Print window sticker</p>
              <PoweredBy className="mt-2" />
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
